/**
 * The subset of Markdown an agent prompt is written in, as data.
 *
 * Parsing lives here and rendering lives in `components/Markdown.jsx`,
 * because the property worth testing is a property of the parse: **nothing is
 * ever dropped.** A hand-written Markdown parser fails by quietly eating
 * input — an unclosed backtick swallows a paragraph, an unrecognised
 * construct vanishes — and in a console where somebody is deciding whether an
 * agent's instructions are acceptable, a renderer that silently removes a
 * sentence is worse than no renderer at all.
 *
 * Split out so `markdown.test.js` can assert that directly without a DOM: the
 * words in, stripped of markers, must equal the words out.
 *
 * Every branch below is total. Anything not recognised is emitted as literal
 * text, including the markers themselves.
 */

export const FENCE = /^\s*```/;
export const HEADING = /^(#{1,6})\s+(.*)$/;
export const BULLET = /^\s*[-*]\s+(.*)$/;
export const NUMBER = /^\s*(\d+)\.\s+(.*)$/;

/**
 * `**bold**`, `*italic*` and `` `code` `` → `[{ kind, text }]`.
 *
 * One pass, left to right. An unmatched opener is not a marker — it is the
 * character it is — which is why this walks the string rather than running
 * three replacements over it. `2 * 3 = 6` has to survive.
 */
export function spans(text) {
  const out = [];
  let buffer = "";
  let i = 0;

  const flush = () => {
    if (buffer) {
      out.push({ kind: "text", text: buffer });
      buffer = "";
    }
  };

  while (i < text.length) {
    const rest = text.slice(i);

    // Code first: a backtick span is literal, so `**` inside one is two
    // asterisks rather than an emphasis marker.
    if (rest[0] === "`") {
      const end = rest.indexOf("`", 1);
      if (end > 1) {
        flush();
        out.push({ kind: "code", text: rest.slice(1, end) });
        i += end + 1;
        continue;
      }
    }

    if (rest.startsWith("**")) {
      const end = rest.indexOf("**", 2);
      if (end > 2) {
        flush();
        out.push({ kind: "strong", text: rest.slice(2, end) });
        i += end + 2;
        continue;
      }
    }

    if (rest[0] === "*") {
      const end = rest.indexOf("*", 1);
      // Requires content, so a line of asterisks stays a line of asterisks.
      if (end > 1) {
        flush();
        out.push({ kind: "em", text: rest.slice(1, end) });
        i += end + 1;
        continue;
      }
    }

    buffer += text[i];
    i += 1;
  }

  flush();
  return out;
}

/**
 * Lines → `[{ kind, ... }]`, grouping consecutive list items into one list.
 *
 * An unclosed fence runs to the end of the input rather than consuming
 * nothing: the text is still shown, which is the rule this file exists for.
 */
export function blocks(text) {
  const lines = String(text ?? "").split(/\r?\n/);
  const out = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    if (FENCE.test(line)) {
      const body = [];
      i += 1;
      while (i < lines.length && !FENCE.test(lines[i])) {
        body.push(lines[i]);
        i += 1;
      }
      i += 1;
      out.push({ kind: "code", body: body.join("\n") });
      continue;
    }

    const heading = HEADING.exec(line);
    if (heading) {
      out.push({ kind: "heading", level: heading[1].length, body: heading[2] });
      i += 1;
      continue;
    }

    if (BULLET.test(line) || NUMBER.test(line)) {
      const ordered = !BULLET.test(line);
      const items = [];
      while (i < lines.length) {
        const bullet = BULLET.exec(lines[i]);
        const number = NUMBER.exec(lines[i]);
        if (!bullet && !number) break;
        // A bullet list and a numbered list next to each other are two
        // lists, not one with a confused marker.
        if (Boolean(bullet) === ordered) break;
        items.push(bullet ? bullet[1] : number[2]);
        i += 1;
      }
      out.push({ kind: "list", ordered, items });
      continue;
    }

    if (!line.trim()) {
      i += 1;
      continue;
    }

    const body = [];
    while (i < lines.length && lines[i].trim()
           && !FENCE.test(lines[i]) && !HEADING.test(lines[i])
           && !BULLET.test(lines[i]) && !NUMBER.test(lines[i])) {
      body.push(lines[i]);
      i += 1;
    }
    out.push({ kind: "paragraph", body: body.join(" ") });
  }

  return out;
}

/** Every word the parse will put on screen, in order. For the loss test. */
export function rendered(text) {
  return blocks(text)
    .map((block) => {
      if (block.kind === "code") return block.body;
      if (block.kind === "list") {
        return block.items.map((i) => spans(i).map((s) => s.text).join("")).join(" ");
      }
      return spans(block.body).map((s) => s.text).join("");
    })
    .join(" ");
}
